import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isSectionRequired, useAppSections, useUploadPendingSupportingDocuments } from "@lumanity/ui/projects";
import { ArrowLeft } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import ProjectForm, { ProjectFormValues } from "@/components/ProjectForm";
import { useCreateProject } from "@/hooks/useProjects";

const CreateProject = () => {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const uploadPending = useUploadPendingSupportingDocuments();
  const { data: sections } = useAppSections();
  const documentsRequired = isSectionRequired(sections, "supporting_documents");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: ProjectFormValues) => {
    setIsSubmitting(true);
    try {
      const newProject = await createProject.mutateAsync({
        name: values.name,
        code: values.code,
        kantataProjectId: values.kantataProjectId || undefined,
        clientId: values.clientIds[0] || undefined,
        clientName: values.clientNames?.[0] || undefined,
        startDate: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        therapyArea: values.therapyAreas.join(", "),
        objective: values.description,
        memberIds: values.memberIds,
        model: values.model,
        isAdvancedRag: values.isAdvancedRag,
        aiPermissions: values.aiPermissions,
      });

      // The project stands even if a file fails to upload. When the app requires supporting
      // documents and none made it, send the user to edit the project to add one.
      const pending = values.pendingSupportingDocuments;
      if (pending.length > 0) {
        const { failed } = await uploadPending.mutateAsync({ projectId: newProject.id, pending });
        const names = failed.map((f) => f.file.name).join(", ");
        if (documentsRequired && failed.length === pending.length) {
          toast.error(`Project created, but its supporting documents failed to upload (${names}). The project needs at least one — add it now.`);
          navigate(`/projects/${newProject.id}/edit`);
          return;
        }
        if (failed.length > 0) {
          toast.error(`Project created, but ${failed.length} supporting document(s) failed to upload: ${names}. Add them from Update Project Setup.`);
        }
      }
      navigate(`/projects/${newProject.id}`);
    } catch {
      toast.error("Could not create the project. Check the required fields and try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout mainClassName="container-md px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-brand-teal hover:underline mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back to Projects
      </Link>

      <h1 className="text-2xl font-bold text-brand-navy mb-2">Initiate New Project</h1>
      <p className="text-muted-foreground mb-8">Enter basic project information to get started. You can amend these fields later throughout the project.</p>

      <ProjectForm
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Continue to Project Setup"
      />
    </PageLayout>
  );
};

export default CreateProject;
